import React, { useState, useEffect } from 'react';
import { Menu, X, Copy, Check } from 'lucide-react';
import Logo from './Logo';
import { SITE, SOCIALS } from '../mock';

const NAV = [
  { label: 'Live', href: '#live' },
  { label: 'Leaderboard', href: '#leaderboard' },
  { label: 'How It Works', href: '#how' },
  { label: 'Payouts', href: '#payouts' },
  { label: 'Rewards', href: '#rewards' },
  { label: 'Community', href: '#community' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar({ stream }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const copyCode = () => {
    navigator.clipboard?.writeText(SITE.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled || open ? '#080B12' : 'transparent',
        backdropFilter: 'none',
        borderBottom: scrolled ? '1px solid var(--ar-border)' : '1px solid transparent',
        boxShadow: scrolled ? '0 6px 20px -14px rgba(0,0,0,0.9)' : 'none',
      }}
    >
      <div className="ar-container flex items-center justify-between h-[72px]">
        <a href="#home" className="flex items-center gap-2.5" aria-label="AsianRisk home" data-testid="nav-home-link">
          <Logo size={40} glow={false} />
          <span className="font-display font-extrabold text-xl tracking-tight">
            ASIAN<span className="text-cyan">RISK</span>
          </span>
          <span className="inline-flex items-center justify-center h-6 px-2 rounded-md font-display font-bold text-[11px] leading-none" style={{ border: '1.5px solid rgba(248,250,252,0.3)', color: 'var(--ar-muted)' }} title="18+ only — play responsibly">18+</span>
        </a>
        {stream?.status === 'live' && (
          <a href={SOCIALS.kick} target="_blank" rel="noopener noreferrer" data-testid="nav-live-badge" aria-label="AsianRisk is live on Kick now — watch"
            className="ml-3 mr-auto lg:mr-4 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full font-display font-bold text-[11px] tracking-wider transition-colors hover:bg-[rgba(34,197,94,0.28)]"
            style={{ background: 'rgba(34,197,94,0.16)', color: '#4ADE80', border: '1px solid rgba(74,222,128,0.55)', boxShadow: '0 0 16px -4px rgba(34,197,94,0.6)' }}>
            <span className="w-2 h-2 rounded-full bg-[#22c55e] ar-pulse" aria-hidden="true" /> LIVE
          </a>
        )}

        <nav className="hidden lg:flex items-center gap-3.5 xl:gap-5" aria-label="Main">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} data-testid={`nav-link-${n.href.slice(1)}`} className="font-display font-semibold text-[13px] tracking-wide uppercase text-muted hover:text-cyan transition-colors whitespace-nowrap">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={SITE.roobetRef} target="_blank" rel="noopener noreferrer" data-testid="nav-open-roobet-btn" className="btn-cyan !py-2.5 !px-5"><img src="/assets/roobet-chip-navy.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" /> OPEN ROOBET</a>
        </div>

        <button type="button" className="lg:hidden text-cyan p-2 -mr-2" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mobile-menu" data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div id="mobile-menu" className="lg:hidden ar-container pb-5" style={{ background: '#080B12' }}>
          <div className="ar-panel p-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a key={n.label} href={n.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${n.href.slice(1)}`} className="font-display font-semibold text-sm tracking-wide uppercase text-muted hover:text-cyan transition-colors py-3">
                {n.label}
              </a>
            ))}
            <div className="ar-divider my-2" />
            <button type="button" onClick={copyCode} data-testid="mobile-menu-copy-code-btn" aria-label={`Copy referral code ${SITE.code}`} className="btn-ghost w-full">
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'COPIED' : `CODE ${SITE.code}`}
            </button>
            <a href={SITE.roobetRef} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-open-roobet-btn" className="btn-cyan w-full mt-2"><img src="/assets/roobet-chip-navy.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" /> OPEN ROOBET</a>
          </div>
        </div>
      )}
    </header>
  );
}
